/**
 * BTU sizing engine — calibrated for Jordan's climate (hot/dry summers).
 *
 * Formula:
 *   base = area (m²) × 700  (700 BTU per m² baseline for Jordan)
 *   adjustments:
 *     • orientation: +20% if room is sunny / west-facing, −10% if shaded
 *     • ceiling height: +5% per 0.5m above 2.7m (standard)
 *     • people > 2: +600 BTU per extra person
 *     • kitchen: +4000 BTU (extra heat load)
 *     • top floor / under roof: +10%
 *     • appliances (TV, PC, server): +1000 BTU
 *
 * The total is then snapped to the nearest standard A/C size
 * (9k / 12k / 18k / 24k / 30k / 36k / 48k / 60k BTU).
 *
 * Returns: { needed, recommended, label, tons, breakdown }
 */
export const STANDARD_BTUS = [9000, 12000, 18000, 24000, 30000, 36000, 48000, 60000];

export const BTU_TONS = {
  9000: "1 Ton",
  12000: "1 Ton",
  18000: "1.5 Ton",
  24000: "2 Ton",
  30000: "2.5 Ton",
  36000: "3 Ton",
  48000: "4 Ton",
  60000: "5 Ton",
};

const round100 = (n) => Math.round(n / 100) * 100;

export function calculateBtu({
  area = 20,            // m²
  sunny = false,        // sunny / west-facing room
  shaded = false,       // heavily shaded
  ceilingHeight = 2.7,  // m
  people = 2,
  kitchen = false,
  topFloor = false,
  electronics = false,
}) {
  const a = Math.max(4, Math.min(400, Number(area) || 0));

  let needed = a * 700;
  const breakdown = [{ label: "base", note: `${a} m² × 700`, btu: needed }];

  if (sunny) {
    const add = needed * 0.2;
    needed += add;
    breakdown.push({ label: "sunny", note: "+20%", btu: add });
  } else if (shaded) {
    const add = needed * -0.1;
    needed += add;
    breakdown.push({ label: "shaded", note: "−10%", btu: add });
  }

  const ch = Math.max(2.2, Math.min(6, Number(ceilingHeight) || 2.7));
  if (ch > 2.7) {
    const factor = ((ch - 2.7) / 0.5) * 0.05;
    const add = needed * factor;
    needed += add;
    breakdown.push({ label: "high_ceiling", note: `${ch.toFixed(1)}m`, btu: add });
  }

  const p = Math.max(1, Math.min(20, Number(people) || 2));
  if (p > 2) {
    const add = (p - 2) * 600;
    needed += add;
    breakdown.push({ label: "occupants", note: `+${p - 2} people`, btu: add });
  }

  if (kitchen) {
    needed += 4000;
    breakdown.push({ label: "kitchen", note: "+4000", btu: 4000 });
  }
  if (topFloor) {
    const add = needed * 0.1;
    needed += add;
    breakdown.push({ label: "top_floor", note: "+10%", btu: add });
  }
  if (electronics) {
    needed += 1000;
    breakdown.push({ label: "electronics", note: "+1000", btu: 1000 });
  }

  needed = round100(needed);

  // Pick the SMALLEST standard size that meets or exceeds the need,
  // but warn if customer's value is at the very edge of a size.
  let recommended = STANDARD_BTUS[STANDARD_BTUS.length - 1];
  for (const s of STANDARD_BTUS) {
    if (s >= needed) { recommended = s; break; }
  }

  return {
    needed,
    recommended,
    label: `${recommended.toLocaleString()} BTU · ${BTU_TONS[recommended]}`,
    tons: BTU_TONS[recommended],
    breakdown,
  };
}

/**
 * Find products from a catalog whose variants match the recommended BTU
 * (or come within ±15 % so we don't show empty results).
 */
export function matchProducts(products, recommendedBtu, limit = 6) {
  if (!Array.isArray(products) || !recommendedBtu) return [];

  const scored = [];
  for (const p of products) {
    const variants = p.variants || [];
    let best = null;
    for (const v of variants) {
      // Try several places where a BTU number might live, then strip any
      // thousand-separators so "18,000 BTU" or "18000 BTU" both work.
      const cleanLabel = String(v.label || "").replace(/,/g, "");
      const cleanId = String(v.id || "").replace(/,/g, "");
      const fromLabel = cleanLabel.match(/(\d{3,6})\s*BTU/i)
        || cleanLabel.match(/(\d{3,6})\s*(?:Ton|طن)?\s*BTU/i);
      const fromId = cleanId.match(/::(\d{3,6})/);
      const fromField = v.btu ? String(v.btu).replace(/,/g, "").match(/\d{3,6}/) : null;
      const m = fromLabel || fromId || fromField;
      const vBtu = m ? Number(m[1] ?? m[0]) : null;
      if (!vBtu) continue;
      const diff = Math.abs(vBtu - recommendedBtu);
      if (!best || diff < best.diff) best = { variant: v, btu: vBtu, diff };
    }
    if (best && best.diff <= recommendedBtu * 0.2) {
      scored.push({ product: p, ...best });
    }
  }
  scored.sort((a, b) => a.diff - b.diff || (a.product.price || 0) - (b.product.price || 0));
  return scored.slice(0, limit);
}

/**
 * Suggest realistic multi-unit combinations to cover a target BTU.
 *
 * Returns 2–5 combos like:
 *   [{ units: [36000],          total: 36000, count: 1 },
 *    { units: [18000, 18000],   total: 36000, count: 2 },
 *    { units: [12000, 24000],   total: 36000, count: 2 },
 *    { units: [12000, 12000, 12000], total: 36000, count: 3 }]
 *
 * Each combo's `units` is sorted ascending so identical layouts collapse.
 * The list is deduplicated, ranked by "useful-ness" (fewer units first,
 * then closest to target), and tagged with a useCase key for the UI.
 */
export function suggestCombinations(targetBtu) {
  if (!targetBtu || targetBtu < STANDARD_BTUS[0]) return [];
  const target = targetBtu;
  // Allow up to +25% over-spec so we capture e.g. 2x18k for a 36k target,
  // but reject anything below target (under-cooling is the real failure).
  const upperBound = Math.ceil(target * 1.25);
  const maxUnits = 4;

  const combos = new Map();
  const sortKey = (arr) => [...arr].sort((a, b) => a - b).join(",");

  const recurse = (idx, current, sum) => {
    if (sum >= target && sum <= upperBound) {
      const k = sortKey(current);
      if (!combos.has(k)) {
        combos.set(k, [...current].sort((a, b) => a - b));
      }
    }
    if (sum >= target || current.length >= maxUnits) return;
    for (let i = idx; i < STANDARD_BTUS.length; i++) {
      const s = STANDARD_BTUS[i];
      // Skip sizes that would push us way over upperBound on first add
      if (sum + s > upperBound + STANDARD_BTUS[0]) continue;
      current.push(s);
      recurse(i, current, sum + s);
      current.pop();
    }
  };
  recurse(0, [], 0);

  // Annotate, rank, trim
  const list = Array.from(combos.values()).map((units) => {
    const total = units.reduce((a, b) => a + b, 0);
    const count = units.length;
    // useCase classification for the UI's plain-language hint
    let useCase = "balanced";
    if (count === 1) useCase = "single";
    else if (units.every((u) => u === units[0])) useCase = "equal";
    else if (count >= 3) useCase = "apartment";
    else useCase = "mixed";
    return { units, total, count, useCase, over: total - target };
  });

  // Sort by: fewest units first → closest to target → smallest spread
  list.sort((a, b) => {
    if (a.count !== b.count) return a.count - b.count;
    if (a.over !== b.over) return a.over - b.over;
    return Math.max(...a.units) - Math.max(...b.units);
  });

  // Keep at most one combo per `count` family to avoid clutter, plus the
  // single-unit option if it's not already first.
  const seenByCount = new Set();
  const out = [];
  for (const c of list) {
    if (seenByCount.has(c.count)) continue;
    seenByCount.add(c.count);
    out.push(c);
    if (out.length >= 4) break;
  }
  return out;
}
